import * as React from 'react';
import styled, { css } from 'styled-components';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faHourglassHalf } from '@fortawesome/pro-regular-svg-icons/faHourglassHalf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import isEqual from 'react-fast-compare';
import RegisterLink from '@/components/RegisterLink/RegisterLink';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    itemId: number;
};

const CardCtaBanner = (props: Props) => {
    const { catalogId, itemId } = props;

    const { isCatalogOnly = false } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { isAvailable = false } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { approved = false, declined = false, pending = false, registered = false } = useSelector(
        (state) => getCatalogRegistration(state, catalogId),
        isEqual
    );

    if (!isAvailable) {
        return null;
    } else if (approved) {
        return (
            <ApprovedDiv>
                <FontAwesomeIcon icon={faCheckCircle} />
                <FormattedMessage id="approved_to_bid" />
            </ApprovedDiv>
        );
    } else if (declined) {
        return (
            <DeclinedDiv>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <FormattedMessage id="registration_status_label.registration_declined" />
            </DeclinedDiv>
        );
    } else if (pending && isCatalogOnly) {
        return (
            <PendingDiv>
                <FontAwesomeIcon icon={faHourglassHalf} />
                <FormattedMessage id="registration_status_label.catalog_only_registration_submitted" />
            </PendingDiv>
        );
    } else if (pending) {
        return (
            <PendingDiv>
                <FontAwesomeIcon icon={faHourglassHalf} />
                <FormattedMessage id="registration_status_label.registration_pending" />
            </PendingDiv>
        );
    } else if (!registered) {
        return (
            <RegisterDiv>
                <StyledRegisterLink catalogId={catalogId} />
            </RegisterDiv>
        );
    }

    return null;
};

export default React.memo<Props>(CardCtaBanner);

const BannerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 26px;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;

const ApprovedDiv = styled.div`
    ${BannerStyle}
    color: ${({ theme }) => theme.colors.teal};
    & > span {
        word-break: keep-all;
        display: inline-block;
        height: 26px;
        overflow: hidden;
        line-height: 26px;
        width: 90px;
    }
`;

const DeclinedDiv = styled.div`
    ${BannerStyle}
    color: ${({ theme }) => theme.colors.red100};
`;

const PendingDiv = styled.div`
    ${BannerStyle}
    color: ${({ theme }) => theme.colors.grey100};
    & > span {
        word-break: keep-all;
        height: 26px;
        overflow: hidden;
        line-height: 26px;
        width: 74px;
        display: flex;
        flex-direction: column-reverse;
    }
`;

const RegisterDiv = styled.div`
    ${BannerStyle}
    .register-link {
        text-transform: none;
    }
`;

const StyledRegisterLink = styled(RegisterLink)`
    text-transform: none;
`;
